import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api, formatTL } from "@/lib/api";
import { Home, ShoppingCart, ClipboardList, User, LogOut, Search, CreditCard, Wallet, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import DijitalRoketFooter from "@/components/common/DijitalRoketFooter";
import Logo from "@/components/common/Logo";

const menu = [
  { to: "/b2b", end: true, label: "Anasayfa", icon: Home, testid: "nav-home" },
  { to: "/b2b/siparisler", label: "Siparişlerim", icon: ClipboardList, testid: "nav-orders" },
  { to: "/b2b/cari-hesap", label: "Cari Hesap", icon: Wallet, testid: "nav-cari" },
  { to: "/b2b/hesabim", label: "Hesabım", icon: User, testid: "nav-account" },
  { to: "/b2b/sepet", label: "Sepetim", icon: ShoppingCart, testid: "nav-cart" },
];

export default function CompanyLayout() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const loc = useLocation();
  const [company, setCompany] = useState(null);
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/companies/me/self").then(r => setCompany(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const handleLogout = async () => { await logout(); nav("/login"); };
  const doSearch = (e) => {
    e.preventDefault();
    nav(`/b2b?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen bg-slate-950 grain-bg flex-1 flex flex-col w-full">
      {/* Top nav */}
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 h-14 sm:h-16 flex items-center gap-2 sm:gap-4">
          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-slate-200 hover:bg-slate-800 shrink-0" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 bg-slate-950 border-slate-800 text-slate-50">
              <div className="h-full flex flex-col">
                <div className="h-14 px-4 flex items-center gap-3 border-b border-slate-800">
                  <Logo size="sm" />
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 border-l border-slate-800 pl-3">B2B<br/>Bayi</div>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                  {menu.map((m) => (
                    <NavLink
                      key={m.to}
                      to={m.to}
                      end={m.end}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`${m.testid}-mobile`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-sm transition-colors ${
                          isActive
                            ? "bg-slate-800 text-slate-50 border-l-2 border-red-500 pl-2"
                            : "text-slate-300 hover:text-slate-50 hover:bg-slate-800/60"
                        }`
                      }
                    >
                      <m.icon className="w-4 h-4 shrink-0" /> <span>{m.label}</span>
                      {m.to === "/b2b/sepet" && count > 0 && (
                        <Badge className="ml-auto bg-red-600 text-white h-5 min-w-5 px-1.5 rounded-full">{count}</Badge>
                      )}
                    </NavLink>
                  ))}
                </nav>
                <div className="p-4 border-t border-slate-800">
                  <div className="text-xs text-slate-400 truncate mb-2">{user?.email}</div>
                  <Button variant="ghost" onClick={handleLogout}
                    className="w-full justify-start text-slate-300 hover:bg-red-900/30 hover:text-red-400">
                    <LogOut className="w-4 h-4 mr-2" /> Güvenli Çıkış
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/b2b" data-testid="logo-home-link" className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Logo size="sm" />
            <div className="hidden sm:block text-[10px] uppercase tracking-widest text-slate-500 border-l border-slate-800 pl-3">B2B<br/>Bayi</div>
          </Link>

          <form onSubmit={doSearch} className="flex-1 max-w-2xl relative hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="global-search-input"
              placeholder="Ürün adı, stok kodu, OEM, marka…"
              className="pl-10 h-10 bg-slate-900 border-slate-800 text-slate-50 focus-visible:border-red-500 focus-visible:ring-red-500"
            />
          </form>

          {/* Desktop nav */}
          <nav className="hidden md:flex ml-auto items-center gap-1.5">
            {menu.map((m) => (
              <NavLink key={m.to} to={m.to} end={m.end} data-testid={m.testid}
                className={({ isActive }) =>
                  `group relative px-3.5 py-2 text-sm font-medium rounded-full border flex items-center gap-2 transition-all duration-200 ${
                    isActive
                      ? "bg-red-600 border-red-500 text-white shadow-[0_0_0_3px_rgba(220,38,38,0.15)]"
                      : "bg-slate-900/70 border-slate-700/70 text-slate-300 hover:bg-slate-800 hover:border-slate-600 hover:text-white hover:-translate-y-0.5"
                  }`}>
                <m.icon className="w-4 h-4 shrink-0" />
                <span className="hidden lg:inline">{m.label}</span>
                {m.to === "/b2b/sepet" && count > 0 && (
                  <Badge className="bg-white text-red-600 h-5 min-w-5 px-1.5 rounded-full text-[11px] font-bold">{count}</Badge>
                )}
              </NavLink>
            ))}
            <button type="button" onClick={handleLogout} data-testid="logout-button"
              className="ml-1 px-3.5 py-2 text-sm font-medium rounded-full border border-slate-700/70 bg-slate-900/70 text-slate-300 flex items-center gap-2 transition-all duration-200 hover:bg-red-900/40 hover:border-red-700 hover:text-red-300 hover:-translate-y-0.5">
              <LogOut className="w-4 h-4" /> <span className="hidden lg:inline">Çıkış</span>
            </button>
          </nav>

          {/* Mobile: sepet ikon + rozet, sağ tarafta */}
          <NavLink to="/b2b/sepet" data-testid="nav-cart-mobile"
            className="md:hidden ml-auto relative px-2 py-2 text-slate-300 hover:text-slate-50">
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <Badge className="absolute -top-1 -right-1 bg-red-600 text-white h-4 min-w-4 px-1 rounded-full text-[10px]">{count}</Badge>
            )}
          </NavLink>
        </div>

        {/* Mobil arama satırı */}
        <div className="md:hidden border-t border-slate-800 bg-slate-950/95 px-3 py-2">
          <form onSubmit={doSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="global-search-input-mobile"
              placeholder="Ürün, stok kodu, OEM…"
              className="pl-10 h-10 bg-slate-900 border-slate-800 text-slate-50 focus-visible:border-red-500 focus-visible:ring-red-500"
            />
          </form>
        </div>
      </header>

      {/* Company strip */}
      {company && (
        <div className="border-b border-slate-800 bg-slate-900/50">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs sm:text-sm">
            <div className="text-slate-400">Firma: <span className="text-slate-50 font-medium">{company.name}</span></div>
            <div className="text-slate-400">İskonto: <span className="text-red-500 font-semibold" data-testid="company-discount">%{company.discount_rate}</span></div>
            <div className="text-slate-400">Bakiye: <span className={company.balance < 0 ? "text-amber-500 font-semibold" : company.balance > 0 ? "text-emerald-500 font-semibold" : "text-slate-300 font-semibold"} data-testid="company-balance">{formatTL(company.balance)}</span></div>
            {company.credit_limit != null && (
              <div className="text-slate-400">
                Cari Limit: <span className="text-slate-300 font-semibold" data-testid="company-credit-limit">{formatTL(company.credit_limit)}</span>
                <span className="text-slate-500 mx-2">•</span>
                Kalan: <span className={`font-semibold ${(company.credit_limit + company.balance) <= 0 ? "text-red-500" : "text-emerald-500"}`} data-testid="company-credit-available">{formatTL(Math.max(0, company.credit_limit + company.balance))}</span>
              </div>
            )}
            {company.payment_url && (
              <a
                href={company.payment_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cari-payment-button"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-red-600 hover:bg-red-500 text-white text-xs font-semibold uppercase tracking-widest transition-colors ml-auto"
              >
                <CreditCard className="w-3.5 h-3.5" /> Cari Ödeme Yap
              </a>
            )}
          </div>
        </div>
      )}

      <main className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 w-full flex-1 min-w-0">
        <Outlet context={{ company }} />
      </main>
      <footer className="border-t border-slate-800 mt-8">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6">
          <DijitalRoketFooter />
        </div>
      </footer>
    </div>
  );
}
